import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Box, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Input from "@ui/input/Input";
import Panel from "@ui/panel/Panel";
import Spacer from "@ui/spacer/Spacer";
import { Rows } from "@ui/table/Rows";
import Signal from "badmfck-signal";
import { FC, useCallback, useState } from "react";
import { GD } from "src/GD";
import { S_FRAUD_RULE_POPUP } from "./FraudRulePopup";
import { TFraudGroup, TFraudRule } from "./types";
import { S_TEST_RULES_POPUP } from "./TestRules";

export const S_FRAUD_GROUPS_POPUP = new Signal<Record<string, unknown> | null>("S_FRAUD_GROUPS_POPUP");

export const FraudGroupsPopup: FC = () => {
	useLocale();
	const requestData = S_FRAUD_GROUPS_POPUP.use();
	const [isNewGroupOpen, setIsNewGroupOpen] = useState(false);
	const [editGroupName, setEditGroupName] = useState<null | string>(null);
	const [editGroupId, setEditGroupId] = useState<null | number>(null);
	const [groups] = GD.REQ_FRAUD_GROUPS_GET.useValue(undefined, undefined, [requestData]);

	const closePopup = useCallback(() => {
		S_FRAUD_GROUPS_POPUP.invoke(null);
	}, []);

	if (!requestData) return null;

	return (
		<>
			<Panel
				sx={{
					minWidth: "850px",
					maxWidth: "950px",
					height: "90vh",
				}}
				decorator="absolute"
				label={msg("fraudRules")}
				onClose={closePopup}>
				<Box sx={{ height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}>
					<Rows<TFraudGroup>
						data={groups as TFraudGroup[]}
						order={["name"]}
						gridTemplateColumns="auto 1fr auto auto"
						internal={false}
						onExpand={Rule}
						menu={[
							{ title: msg("edit"), id: "edit" },
							{ title: msg("delete"), id: "delete" },
						]}
						onMenuClick={async (id, row) => {
							if (id === "delete") {
								await GD.REQ_FRAUD_GROUP_DELETE.request({ groupID: row.id });
							} else if (id === "edit") {
								setEditGroupName(row.name);
								setEditGroupId(row.id);
							}
						}}
					/>

					<Box sx={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
						<Button
							variant="secondary"
							onClick={() => {
								S_TEST_RULES_POPUP.invoke({ target_id: 0, target_type: "project" });
							}}>{msg("testRuleSGroup")}</Button>
						<Button
							variant="secondary"
							onClick={() => {
								setIsNewGroupOpen(true);
							}}>{msg("addGroup")}</Button>
						<Button
							onClick={() => {
								S_FRAUD_RULE_POPUP.invoke({ ruleID: -1 });
							}}>{msg("addRule")}</Button>
					</Box>
				</Box>
			</Panel>
			<NewGroup
				isOpen={isNewGroupOpen || !!editGroupName}
				onClose={() => {
					setIsNewGroupOpen(false);
					setEditGroupName(null);
					setEditGroupId(null);
				}}
				initValue={editGroupName ? editGroupName : undefined}
				editGroupId={editGroupId || undefined}
			/>
		</>
	);
};

const Rule = (row: { rules: TFraudRule[] }): JSX.Element | null => {
	useLocale();
	return (
		<Rows<TFraudRule>
			internal
			data={row.rules}
			gridTemplateColumns="auto 1fr auto auto auto"
			order={["name", "action", "status"]}
			menu={[
				{ title: msg("edit"), id: "edit" },
				{ title: msg("delete"), id: "delete" },
			]}
			onClick={row => S_FRAUD_RULE_POPUP.invoke({ ruleID: row.id, previewMode: 1 })}
			onMenuClick={async (id, row) => {
				if (id === "delete") {
					await GD.REQ_FRAUD_RULE_DELETE.request({ ruleID: row.id });
				} else if (id === "edit") {
					S_FRAUD_RULE_POPUP.invoke({ ruleID: row.id });
				}
			}}
		/>
	);
};

const NewGroup: FC<{ isOpen: boolean; onClose: () => void; initValue?: string; editGroupId?: number }> = ({ isOpen, onClose, initValue, editGroupId }) => {
	useLocale();
	const [dataInput, setDataInput] = useState<{ [key: string]: string | number | boolean }>({ groupName: initValue ?? "" });

	if (!isOpen) return null;

	return (
		<Panel decorator="absolute" label={editGroupId ? msg("editGroup") : msg("newGroup")} sx={{ minWidth: "600px", maxWidth: "400px", height: "45vh" }} onClose={onClose}>
			<VBox sx={{ height: "100%" }}>
				<Input type="text" label={msg("groupName")} name="groupName" data={dataInput} onChange={val => setDataInput({ groupName: val })} />
				<Spacer size="20px" />
				{/*
				<Select<{ name: string; value: string }>
					name="rules"
					// data={dataSelect}
					value={selectedRule || dataSelect[0]}
					values={dataSelect}
					onChange={val => setSelectedRule(val)}
				/>
                */}
				<Spacer grow minHeight="20px" />
				<Box sx={{ display: "flex", gap: "8px", justifyContent: "flex-end" }}>
					<Button variant="secondary" onClick={onClose}>{msg("cancel")}</Button>
					<Button
						onClick={async () => {
							if (initValue && editGroupId) {
								await GD.REQ_FRAUD_GROUP_SET.request({ name: dataInput.groupName.toString(), groupID: editGroupId });
							} else {
								await GD.REQ_FRAUD_GROUP_SET.request({ name: dataInput.groupName.toString(), groupID: -1 });
							}
							onClose();
						}}>
						{initValue ? msg("editGroup_dd5f2d9e") : msg("addGroup_94901258")}
					</Button>
				</Box>
			</VBox>
		</Panel>
	);
};
